import React, { FunctionComponent, useEffect, useRef, Fragment, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAiAssistantSuggestions } from '../../helpers/hooks/ai-assistant';
import '../../styles/ai-assistant.styles.scss';

type Props = {
	field: 'main_title' | 'seo_title' | 'summary';
	onSelect: (value: string) => void;
};

const AiAssistantSuggestionsContainer: FunctionComponent<Props> = ({ field, onSelect }) => {
	const [t] = useTranslation();
	const { suggestions, articleValue, valueLengths, handleSelect, handleEdit, selectedIndex } = useAiAssistantSuggestions(field);

	const textareaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
	const [editingIndex, setEditingIndex] = useState<number | null>(null);

	useEffect(() => {
		textareaRefs.current.forEach((ref) => {
			if (ref) {
				ref.style.height = 'auto';
				ref.style.height = `${ref.scrollHeight}px`;
			}
		});
	}, [suggestions]);

	useEffect(() => {
		return () => {
			textareaRefs.current = [];
		};
	}, []);

	const createHandleEdit = useCallback(
		(index: number) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
			handleEdit(e.target.value, index);
		},
		[handleEdit],
	);

	return (
		<div className='ai-tools-title-results-wrapper'>
			{articleValue && (
				<div className='ai-tools-title-results-current-container'>
					<div className='ai-tools-title-results-current-label'>
						<span>{t('current')}:</span>
						<span className='ai-tools-title-results-current-value-label'>{articleValue}</span>
					</div>
				</div>
			)}

			<div className='ai-tools-title-suggestions-container'>
				{suggestions.map((suggestion, index) => {
					const isEmpty = suggestion.trim() === '';
					const isTooLong =
						field === 'summary' ? valueLengths[index] > 500 : field === 'seo_title' ? valueLengths[index] > 60 : valueLengths[index] > 255;

					const hasError = isEmpty || isTooLong;
					const isSelected = selectedIndex === index;
					const isBeingEdited = editingIndex === index;

					return (
						<Fragment key={index}>
							<div
								className={`ai-tools-title-suggestion-text-container ${hasError ? 'ai-tools-title-suggestion-text-error-state' : ''}`}
								style={isBeingEdited ? { borderBottom: '2px solid #1262FA' } : {}}
							>
								<div className='ai-tools-title-text-container'>
									<input
										type='radio'
										name={`${field}_radio`}
										className='ai-tools-title-radio'
										checked={isSelected}
										onClick={(e) => {
											e.preventDefault(); // Prevent default radio selection
											if (isSelected) {
												handleSelect('', -1); // Deselect
												onSelect('');
											} else {
												handleSelect(suggestion, index);
												onSelect(suggestion);
											}
										}}
										disabled={hasError}
									/>
									<textarea
										className='ai-tools-title-textarea'
										value={suggestion}
										ref={(el) => (textareaRefs.current[index] = el)}
										onFocus={() => setEditingIndex(index)}
										onBlur={() => setEditingIndex(null)}
										onChange={createHandleEdit(index)}
									/>
								</div>

								<div className='ai-tools-title-buttons-container'>
									<span className='ai-tools-title-length'>{valueLengths[index]}</span>
								</div>
							</div>

							{isEmpty && (
								<div className='ai-tools-title-textarea-error-container-below'>
									<span className='ai-tools-title-textarea-error-icon' />
									<span className='ai-tools-title-textarea-error-message'>{t('ai_tools_suggestions_error')}</span>
								</div>
							)}

							{isTooLong && (
								<div className='ai-tools-title-textarea-error-container-below'>
									<span className='ai-tools-title-textarea-error-icon' />
									<span className='ai-tools-title-textarea-error-message'>
										{field === 'summary'
											? t('ai_assistant_summary_length_error')
											: field === 'seo_title'
											? t('ai_assistant_seo_title_length_error')
											: t('ai_assistant_text_length_error')}
									</span>
								</div>
							)}
						</Fragment>
					);
				})}
			</div>
		</div>
	);
};

export default AiAssistantSuggestionsContainer;
